import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CalendarDays, Sparkles, Repeat, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SHIFT_COLORS = {
  day: { bg: '#2b2b33', text: '#fff' },
  swing: { bg: '#D99E22', text: '#fff' },
  evening: { bg: '#6D28D9', text: '#fff' },
  overnight: { bg: '#1E40AF', text: '#fff' },
};

const fmtDay = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const weekLabel = (start) => {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(s); e.setDate(s.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
};

const weekStartOf = (d) => {
  const dt = new Date(d + 'T00:00:00');
  dt.setDate(dt.getDate() - dt.getDay());
  return dt.toISOString().slice(0, 10);
};

export default function MySchedule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/shifts/my`)
      .then((res) => setData(res.data))
      .catch((err) => { if (err.response?.status === 403) setNotLinked(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  if (notLinked) {
    return (
      <Card className="p-10 text-center max-w-lg mx-auto" data-testid="myschedule-not-linked">
        <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-1">Account not linked</h2>
        <p className="text-sm text-muted-foreground">Your login is not linked to a roster employee. Ask an administrator to link your account from the Team page.</p>
      </Card>
    );
  }

  const shifts = data?.shifts || [];
  const byWeek = {};
  shifts.forEach((s) => {
    const wk = weekStartOf(s.date);
    (byWeek[wk] = byWeek[wk] || []).push(s);
  });
  const weekKeys = Object.keys(byWeek).sort();

  return (
    <div className="space-y-5" data-testid="my-schedule-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My Schedule</h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="my-schedule-subtitle">
          Your published shifts for {data?.member?.name} — current week onward. Read-only; contact an admin for changes.
        </p>
      </div>

      {weekKeys.length === 0 && (
        <Card className="p-10 text-center" data-testid="my-schedule-empty">
          <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold mb-1">No upcoming shifts</h3>
          <p className="text-sm text-muted-foreground">You have no shifts scheduled from this week onward. Check back after the next schedule is built.</p>
        </Card>
      )}

      {weekKeys.map((wk) => (
        <Card key={wk} className="p-4 sm:p-5" data-testid={`my-schedule-week-${wk}`}>
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            Week of {weekLabel(wk)}
            <Badge variant="outline" className="text-[10px]">{byWeek[wk].length} shift{byWeek[wk].length === 1 ? '' : 's'}</Badge>
          </div>
          <div className="space-y-2">
            {byWeek[wk].map((s, i) => {
              const color = s.kind === 'manual'
                ? { bg: '#92400E', text: '#fff' }
                : (SHIFT_COLORS[s.shift_type] || { bg: '#2b2b33', text: '#fff' });
              return (
                <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border p-3" data-testid={`my-shift-${s.date}-${s.shift_type || 'manual'}`}>
                  <div className="min-w-[150px]">
                    <div className="text-sm font-semibold">{fmtDay(s.date)}</div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: color.bg, color: color.text }}>
                    {s.display_name}
                  </span>
                  {s.start_time && s.end_time && (
                    <span className="text-xs text-muted-foreground">{s.start_time} – {s.end_time}</span>
                  )}
                  <div className="flex flex-wrap gap-1.5 ml-auto">
                    {s.kind === 'manual' && (
                      <Badge className="text-[10px] bg-[#92400E] hover:bg-[#92400E]"><Sparkles className="h-3 w-3 mr-1" />Custom</Badge>
                    )}
                    {s.role === 'secondary' && (
                      <Badge variant="secondary" className="text-[10px]"><Users className="h-3 w-3 mr-1" />Secondary</Badge>
                    )}
                    {s.is_switch && (
                      <Badge variant="outline" className="text-[10px] border-emerald-600 text-emerald-700"><Repeat className="h-3 w-3 mr-1" />Switched{s.switch_from ? ` from ${s.switch_from}` : ''}</Badge>
                    )}
                    {s.partner && (
                      <Badge variant="outline" className="text-[10px]">with {s.partner}</Badge>
                    )}
                  </div>
                  {s.notes && <div className="w-full text-xs italic text-muted-foreground">{s.notes}</div>}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
